<template>
  <div class="card">
    <img class="logo" :src="getLogoImage()" alt="text-to-speech icon" />

    <h4>
      Saldo disponible {{ getTypeName(type) }}

      <a-tooltip :title="getInfoTooltipByType(type)" placement="right">
        <InfoCircleOutlined class="icon" />
      </a-tooltip>
    </h4>

    <p v-show="billingType" class="balance">
      <span v-if="billingType === BillingType.Prepaid" :class="{ negative: hasNegativeBalance }">
        ${{ balance }}
      </span>
      <span v-if="billingType === BillingType.Postpaid">Ilimitado ∞</span>

      <a-tooltip v-if="hasNegativeBalance" :title="getWarningTooltipByType(type)" placement="right">
        <WarningOutlined class="icon-warning" />
      </a-tooltip>

      <a-button
        v-if="requiredUpgrade"
        style="padding: 0"
        type="link"
        @click="handleOpenRestriction(productType)"
      >
        <a-tag class="tag_upgrade" :color="ActionPlanStrategyColor[ActionPlanStrategy.Upgrade]">
          {{ ActionPlanStrategyText[ActionPlanStrategy.Upgrade] }}
        </a-tag>
      </a-button>
    </p>
    <!-- </div> -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, inject, Ref } from "vue";
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons-vue";

import { BillingType, ProductType } from "@/@types/settings/billing";
import { fetchWhatsappBalance } from "@/app/settings/services";
import { ActionPlanStrategy, ActionPlanStrategyColor, ActionPlanStrategyText } from "@/app/common/utils/enums";

type TypeName = "calls" | "whatsapp" | "whatsapp-voice" | "sms" | "botAi" | "tts";

const props = defineProps<{ type: TypeName }>();

const balance = ref(0);
const billingType = ref<BillingType>();
// Tasa de IGV que el backend aplica a las recargas de este producto.
// Llega undefined en los productos que no tienen IGV propio: esos caen al
// flag del plan que ya usa BalanceRechargeCard.
const igvRate = ref<number | undefined>(undefined);

const hasSms = inject<Ref<Boolean>>("hasSms", ref(false));
const hasWhatsapp = inject<Ref<Boolean>>("hasWhatsapp", ref(false));
const hasVoice = inject<Ref<Boolean>>("hasVoice", ref(false));

const requiredUpgrade = computed(() => {
  console.log("hasWhatsapp.value:", hasWhatsapp.value);
  console.log("hasVoice.value:", hasVoice.value);
  console.log("hasSms.value:", hasSms.value);
  switch (props.type) {
    case "whatsapp":
      return !hasWhatsapp.value;

    case "calls":
      return !hasVoice.value;

    case "sms":
      return !hasSms.value;

    default:
      return false;
  }
});

const productType = computed(() => {
  switch (props.type) {
    case "whatsapp":
      return ProductType.WhatsApp;

    case "calls":
      return ProductType.Voice;

    case "sms":
      return ProductType.Sms;
    case "tts":
      return ProductType.TextToSpeech;

    default:
      return "";
  }
});

const handleOpenRestriction = inject<(type: TypeName) => any>("handleOpenRestriction", () => {});

const getLogoImage = () => {
  return new URL(`../../../shared/assets/channels/${props.type}.svg`, import.meta.url).href;
};

const fetchBalance = async () => {
  console.log("fetchBalance called for type:", props.type);
  const response = await fetchWhatsappBalance(props.type);
  balance.value = response.balance;
  billingType.value = response.type_billing;
  igvRate.value = response.igv_rate;
};

const hasNegativeBalance = computed(() => balance.value < 0);

onMounted(() => {
  fetchBalance();
});

const getTypeName = (type: TypeName) => {
  switch (type) {
    case "whatsapp":
      return "de WhatsApp";

    case "whatsapp-voice":
      return "de WhatsApp Voice"

    case "calls":
      return "para llamadas";

    case "sms":
      return "para SMS";

    case "botAi":
      return "de BotAI Message";

    case "tts":
      return "para Text to Speech";

    default:
      return "";
  }
};

const getInfoTooltipByType = (type: TypeName) => {
  switch (type) {
    case "whatsapp":
      return "Recuerda que si no cuentas con saldo disponible, tus chatbots no atenderán por los canales de WhatsApp configurados en ellos.";

    case "whatsapp-voice":
      return "Si el saldo se acaba, tus canales de WhatsApp Voice pasarán a inactivarse, pausando sus configuraciones. Después de recargar podrás activarlos.";

    case "calls":
      return "Recuerda que si no cuentas con saldo disponible, no podrás realizar llamadas manuales ni estrategias de llamadas masivas.";

    case "sms":
      return "Recuerda que si no cuentas con saldo disponible, no podrás enviar plantillas ni estrategias de SMS masivo.";

    case "botAi":
      return "Recuerda que si no cuentas con saldo disponible, tus bots AI no atenderán por los canales en los que están configurados.";

    case "tts":
      return "Recuerda que si no cuentas con saldo disponible, no podrás iniciar estrategias de IVR.";
      
    default:
      return "";
  }
};

const getWarningTooltipByType = (type: TypeName) => {
  switch (type) {
    case "whatsapp":
      return "Tienes mensajes retenidos por falta de saldo. Para poder liberarlos recarga tu saldo desde este módulo fácil y rápido.";

    case "calls":
      return "Para poder realizar y recibir llamadas o dar play a estrategias recarga tu saldo desde este módulo de forma fácil y rápida.";

    case "sms":
      return "Para poder enviar plantillas o dar play a estrategias recarga tu saldo desde este módulo de forma fácil y rápida.";

    default:
      return "";
  }
};

defineExpose({ fetchBalance, billingType, balance, igvRate });
</script>

<style lang="sass" scoped>
h4, p
  margin: 0
.card
  padding: 16px 24px
  border: 1px solid $gray-5
  border-radius: 8px
  display: flex
  align-items: center
  gap: 14px
  grid-column: 1/3

  h4
    font-size: 14px
    font-weight: 600
    color: $gray-8
    max-width: 138px
    // margin-bottom: 4px

  .balance
    font-size: 30px
    line-height: 34px
    font-weight: 600
    color: $gray-9
    display: flex
    align-items: center
    .negative
      color: $red-6
    .tag_upgrade
      margin-left: 10px

.logo
  width: 40px

.icon
  margin-left: 5px

  &-warning
    color: $gold-6
    margin-left: 12px
</style>
